import type * as Ariakit from '@ariakit/react/menu';
import { useQueryClient } from '@tanstack/react-query';
import {
  maxEndppointOIDCProvidersQueryOptions,
  useEditEndpointOIDCProvider,
} from '#api/endpoint-oidc-providers.queries.ts';
import { trackError } from '#api/errors.ts';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';

type Callbacks = {
  onError: (msg: string | null) => void;
  onConflict: (endpointOIDC: EndpointOIDC | null) => void;
};

export function useToggleEnabled(endpointOIDC: EndpointOIDC, { onError, onConflict }: Callbacks) {
  const editOIDCProviderMutation = useEditEndpointOIDCProvider();
  const queryClient = useQueryClient();

  async function toggleEnabled(menu: Ariakit.MenuStore) {
    // Reset any previous error messages
    onError(null);

    async function toggle() {
      await editOIDCProviderMutation.mutateAsync({
        id: endpointOIDC.id,
        values: {
          clientSecret: null,
          ...endpointOIDC,
          enabled: !endpointOIDC.enabled,
        },
      });
    }

    try {
      // It's easy to disable
      if (endpointOIDC.enabled) {
        await toggle();
        menu.hide();
        return;
      }

      // When enabling, check if there's another enabled provider using the same domain
      const { data: allEndpointOIDCProviders } = await queryClient.fetchQuery(maxEndppointOIDCProvidersQueryOptions);
      const conflictingProviders = allEndpointOIDCProviders.filter(
        (p) => p.id !== endpointOIDC.id && p.enabled && p.domainID === endpointOIDC.domainID
      );

      if (conflictingProviders[0]) {
        onConflict(conflictingProviders[0]);
      } else {
        await toggle();
      }
    } catch (error) {
      trackError(error);
      onError(
        `We couldn’t ${endpointOIDC.enabled ? 'disable' : 'enable'} the provider, please try again or contact support`
      );
    }
    menu.hide();
  }

  return [toggleEnabled, editOIDCProviderMutation] as const;
}
